import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import { AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

// Get today's date in Berlin timezone as YYYY-MM-DD
const getTodayBerlin = () => {
  const now = new Date();
  // Use Intl to get Berlin time
  const berlinTime = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Berlin',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
  return berlinTime; // Returns YYYY-MM-DD
};

export const WeekSubmissionReminder = () => {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  const [missingWeeks, setMissingWeeks] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    if (!user?.id) return;

    // Session key to track if we've already checked/shown this session
    const sessionKey = `ctime_week_reminder_shown_${user.id}`;
    
    // If already checked this session, do nothing
    if (sessionStorage.getItem(sessionKey)) return;

    const checkMissingWeeks = async () => {
      try {
        const response = await api.get('/approvals/my-missing?weeks=4');
        const data = response.data;

        if (data.count > 0) {
          setMissingWeeks(data.missing);

          // Check localStorage for dismissal today
          const dismissKey = `ctime_week_reminder_dismissed_${user.id}`;
          const dismissedDate = localStorage.getItem(dismissKey);
          const today = getTodayBerlin();

          if (dismissedDate !== today) {
            setDialogOpen(true);
          }
        }
        
        // Mark as checked for this session (whether dialog shown or not)
        sessionStorage.setItem(sessionKey, '1');
      } catch (error) {
        console.error('Failed to fetch missing weeks:', error);
        // Still mark as checked to avoid repeated failed requests
        sessionStorage.setItem(sessionKey, '1');
      }
    };

    checkMissingWeeks();
  }, [user?.id, api]);

  const handleGoToTimeTracking = () => {
    // Navigate to oldest missing week
    if (missingWeeks.length > 0) {
      const oldestWeek = missingWeeks[0]; // Already sorted oldest first
      navigate(`/time-tracking?week=${oldestWeek.week_start}`);
    }
    setDialogOpen(false);
    // Do NOT set localStorage - dialog should appear again next app start if still missing
  };

  const handleDismiss = () => {
    // Set localStorage to today's date
    const dismissKey = `ctime_week_reminder_dismissed_${user.id}`;
    const today = getTodayBerlin();
    localStorage.setItem(dismissKey, today);
    setDialogOpen(false);
  };

  const formatWeekRange = (weekStart, weekEnd) => {
    const start = parseISO(weekStart);
    const end = parseISO(weekEnd);
    return `${format(start, 'dd.MM.', { locale: de })}–${format(end, 'dd.MM.', { locale: de })}`;
  };

  const getStatusBadge = (week) => {
    if (week.status === 'abgelehnt') {
      return (
        <Badge variant="destructive" className="bg-red-500/20 text-red-400 border-red-500/30">
          Abgelehnt – bitte nachbessern
        </Badge>
      );
    }
    if (!week.has_entries) {
      return (
        <Badge variant="secondary" className="bg-muted text-muted-foreground">
          Keine Einträge
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="bg-amber-500/20 text-amber-400 border-amber-500/30">
        Nicht eingereicht
      </Badge>
    );
  };

  if (!user) return null;

  return (
    <Dialog 
      open={dialogOpen} 
      onOpenChange={(open) => {
        // Prevent closing by clicking outside or pressing Escape
        if (!open) return;
        setDialogOpen(open);
      }}
    >
      <DialogContent 
        className="bg-card border-border sm:max-w-lg"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader className="text-center sm:text-left">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-amber-500" />
            </div>
            <DialogTitle className="text-xl font-heading font-bold text-foreground">
              Wochen noch nicht eingereicht
            </DialogTitle>
          </div>
          <DialogDescription className="text-muted-foreground">
            Folgende Wochen wurden noch nicht zur Genehmigung eingereicht. Bitte prüfe deine Zeiten und reiche die Wochen ein.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-2">
          {missingWeeks.map((week) => (
            <div
              key={week.week_start}
              className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border border-border"
            >
              <span className="text-foreground font-medium">
                KW {week.iso_week} · {formatWeekRange(week.week_start, week.week_end)}
              </span>
              {getStatusBadge(week)}
            </div>
          ))}
        </div>

        <div className="mt-6 flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
          <Button
            type="button"
            variant="ghost"
            onClick={handleDismiss}
            className="text-muted-foreground"
            data-testid="week-reminder-dismiss-btn"
          >
            Später erinnern
          </Button>
          <Button
            type="button"
            onClick={handleGoToTimeTracking}
            className="bg-indigo-500 hover:bg-indigo-600 text-white"
            data-testid="week-reminder-goto-btn"
          >
            Zur Zeiterfassung
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
